<script lang="ts">
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import type {
    CommunicationPlanChannelGroup,
    CommunicationPlanDateRange,
    CommunicationPlanEmployeeOption,
    CommunicationPlanFilterDisplayMode,
    CommunicationPlanFilterValue,
    CommunicationPlanStatusGroup,
  } from "../../../../_logic/communication-plan-table-contract"
  import {
    COMMUNICATION_PLAN_CHANNEL_OPTIONS,
    COMMUNICATION_PLAN_STATUS_OPTIONS,
    createDefaultCommunicationPlanFilters,
    normalizeCommunicationPlanFilterValue,
  } from "../../_shared/_logic/filters"

  let {
    value = createDefaultCommunicationPlanFilters(),
    filterMode = "compact",
    employees = [],
    employeeLoading = false,
    employeeError = null,
    onChange = () => {},
  }: {
    value?: CommunicationPlanFilterValue
    filterMode?: CommunicationPlanFilterDisplayMode
    employees?: CommunicationPlanEmployeeOption[]
    employeeLoading?: boolean
    employeeError?: string | null
    onChange?: (value: CommunicationPlanFilterValue) => void
  } = $props()

  const employeeOptions = $derived(
    employees.map((employee) => ({ name: employee.label, value: employee.id })),
  )

  const eventValue = (event: Event): unknown => {
    const detail = (event as CustomEvent<{ value?: unknown; values?: unknown }>).detail

    if (detail && typeof detail === "object") {
      if (Array.isArray(detail.values)) return detail.values

      if ("value" in detail) return detail.value
    }

    return (event.target as HTMLInputElement | HTMLSelectElement | null)?.value ?? ""
  }

  const stringArrayValue = (candidate: unknown): string[] =>
    Array.isArray(candidate)
      ? candidate.map((entry) => String(entry ?? "").trim()).filter(Boolean)
      : String(candidate ?? "").trim()
        ? [String(candidate).trim()]
        : []

  const dateValue = (candidate: unknown): Date | null => {
    if (!candidate) return null

    const parsed =
      candidate instanceof Date ? new Date(candidate.getTime()) : new Date(String(candidate))

    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  const dateRangeValue = (candidate: unknown): CommunicationPlanDateRange => {
    if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
      return { start: null, end: null }
    }

    const range = candidate as Record<string, unknown>

    return { start: dateValue(range.start), end: dateValue(range.end) }
  }

  const channelGroupValue = (candidate: unknown): CommunicationPlanChannelGroup[] => {
    const allowed = new Set(COMMUNICATION_PLAN_CHANNEL_OPTIONS.map((option) => option.value))

    return stringArrayValue(candidate).filter((entry): entry is CommunicationPlanChannelGroup =>
      allowed.has(entry as CommunicationPlanChannelGroup),
    )
  }

  const statusGroupValue = (candidate: unknown): CommunicationPlanStatusGroup[] => {
    const allowed = new Set(COMMUNICATION_PLAN_STATUS_OPTIONS.map((option) => option.value))

    return stringArrayValue(candidate).filter((entry): entry is CommunicationPlanStatusGroup =>
      allowed.has(entry as CommunicationPlanStatusGroup),
    )
  }

  const update = (next: Partial<CommunicationPlanFilterValue>) => {
    onChange(normalizeCommunicationPlanFilterValue({ ...value, ...next }))
  }
</script>

<section class="form communication-plan-filters" aria-label="Terminkommunikation filtern">
  <div class="row">
    <Input
      type="text"
      placeholder="Suche"
      value={value.searchQuery}
      brightBackground={true}
      tooltip="Sucht unter anderem in Auftrag, Adresse, Empfänger, Termininformationen, Mitarbeiter und Terminstatus. Mehrere Begriffe werden kombiniert."
      onChange={(event) => update({ searchQuery: String(eventValue(event) ?? "") })}
    />
    <Input
      type="dateRange"
      placeholder="Terminzeitraum"
      value={value.appointmentDateRange}
      brightBackground={true}
      dateGranularity="day"
      tooltip="Zeigt Termine vom Start- bis einschließlich Enddatum."
      onChange={(event) => update({ appointmentDateRange: dateRangeValue(eventValue(event)) })}
    />
    <Input
      type="select"
      placeholder="Status"
      multiple={true}
      value={value.statusGroups}
      options={COMMUNICATION_PLAN_STATUS_OPTIONS}
      brightBackground={true}
      tooltip="Filtert nach geplant, anstehend, aktiv oder erfolglos. Eine leere Auswahl zeigt alle Status."
      onChange={(event) => update({ statusGroups: statusGroupValue(eventValue(event)) })}
    />
  </div>
  {#if filterMode === "detailed"}
    <div class="row detail-row">
      <Input
        type="select"
        placeholder="Mitarbeiter"
        multiple={true}
        value={value.employeeIds}
        options={employeeOptions}
        brightBackground={true}
        disabled={employeeLoading || Boolean(employeeError)}
        tooltip={employeeError ?? "Filtert nach den Mitarbeitern des dargestellten Termins."}
        onChange={(event) => update({ employeeIds: stringArrayValue(eventValue(event)) })}
      />
      <Input
        type="select"
        placeholder="Kanal"
        multiple={true}
        value={value.channelGroups}
        options={COMMUNICATION_PLAN_CHANNEL_OPTIONS}
        brightBackground={true}
        tooltip="Filtert nach E-Mail, mobiler Nachricht, Telefon oder Brief."
        onChange={(event) => update({ channelGroups: channelGroupValue(eventValue(event)) })}
      />
    </div>
  {/if}
</section>

<style lang="less">
  .communication-plan-filters {
    margin-top: 0;
    margin-bottom: 1rem;
  }

  .detail-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.2rem;
    align-items: center;

    :global(.select),
    :global(.input-container) {
      min-width: 0;
      width: 100%;
    }
  }

  @media (max-width: 760px) {
    .detail-row {
      grid-template-columns: minmax(0, 1fr);
    }
  }
</style>
